import { z } from "zod";
import { SimulationObjectiveSchema, SimulationBatchStatusSchema } from "./simulations.js";

// Contrato da interpretação de simulações. Os FATOS (InterpretationFacts) são
// computados SEMPRE pela plataforma (determinístico, zero PII); a `narrative` é enriquecimento
// opcional do modelo. Numéricos `finite().nullable()` (padrão OEE/coerção do repo).

export const ConfidenceLevelSchema = z.enum(["HIGH", "MEDIUM", "LOW", "INSUFFICIENT"]);
export const InterpretationKindSchema = z.enum(["RUN", "COMPARISON", "BATCH"]);
export const InterpretationNarrativeSourceSchema = z.enum(["NONE", "ASSISTANT"]);
export type InterpretationKind = z.infer<typeof InterpretationKindSchema>;

const NullableNum = z.number().finite().nullable();

export const KpiSetSchema = z.object({
  oee: NullableNum,
  availability: NullableNum,
  performance: NullableNum,
  quality: NullableNum,
  estimatedProduction: NullableNum,
});
export type KpiSet = z.infer<typeof KpiSetSchema>;

const VerdictSchema = z.object({
  direction: z.enum(["IMPROVED", "WORSENED", "NEUTRAL", "NO_BASELINE"]),
  oeeDeltaPp: NullableNum, // pontos percentuais vs referência
  productionDelta: NullableNum, // unidades vs referência
  headline: z.string(), // 1 linha PT-BR determinística
});

const NextStepSchema = z.object({
  label: z.string(), // PT-BR
  // acionável: referencia apenas comandos do catálogo (validado no service).
  command: z.object({ name: z.string(), args: z.record(z.unknown()) }).nullable(),
});

export const InterpretationFactsSchema = z.object({
  generatedAt: z.string().datetime(),
  target: z.object({
    kind: InterpretationKindSchema,
    simulationId: z.string().uuid().optional(),
    comparisonAId: z.string().uuid().optional(),
    comparisonBId: z.string().uuid().optional(),
    batchId: z.string().uuid().optional(),
    scenarioId: z.string().uuid().optional(),
    label: z.string(),
  }),
  verdict: VerdictSchema,
  kpis: z.object({ current: KpiSetSchema, reference: KpiSetSchema.nullable() }),
  bottleneck: z
    .object({
      machineNames: z.array(z.string()),
      throughputUnH: NullableNum,
      shifted: z.boolean().nullable(), // mudou vs referência?
    })
    .nullable(),
  stopCost: z
    .object({
      estimated: z.number().finite(),
      currency: z.literal("BRL"),
      method: z.string(),
    })
    .nullable(), // null ⇒ sem dado (honesto)
  confidence: z.object({ level: ConfidenceLevelSchema, causes: z.array(z.string()) }),
  planAdherence: NullableNum, // quando aplicável (backtest de OP)
  comparison: z
    .object({
      // só kind=COMPARISON (AC3 — causa provável da diferença)
      paramDiffs: z.array(
        z.object({ machineName: z.string(), field: z.string(), a: z.unknown(), b: z.unknown() })
      ),
      calibrationDaysA: z.number().int().nullable(),
      calibrationDaysB: z.number().int().nullable(),
      structureChanged: z.boolean().nullable(),
    })
    .nullable(),
  batch: z
    .object({
      // só kind=BATCH
      objective: SimulationObjectiveSchema,
      targetQuantity: z.number().nullable(),
      winner: z
        .object({
          simulationId: z.string().uuid(),
          label: z.string(),
          meetsTarget: z.boolean().nullable(),
          effort: z.number().finite().nullable(), // Σ|deltaPct| não é persistido (recomputável só no lote) ⇒ null
        })
        .nullable(),
      evaluatedCount: z.number().int(),
      status: SimulationBatchStatusSchema,
    })
    .nullable(),
  nextSteps: z.array(NextStepSchema), // ≥1 quando estado permite
});
export type InterpretationFacts = z.infer<typeof InterpretationFactsSchema>;

// Detalhe completo (GET /interpretations/:id — sessão do usuário, canal de tela).
export const InterpretationResponseSchema = z.object({
  id: z.string().uuid(),
  kind: InterpretationKindSchema,
  facts: InterpretationFactsSchema,
  narrative: z.string().nullable(),
  narrativeSource: InterpretationNarrativeSourceSchema,
  createdAt: z.string().datetime(),
});
export type InterpretationResponse = z.infer<typeof InterpretationResponseSchema>;

// Item do histórico (GET /simulations/:id/interpretations).
export const InterpretationListItemSchema = z.object({
  id: z.string().uuid(),
  kind: InterpretationKindSchema,
  headline: z.string(), // facts.verdict.headline (resumo p/ a lista)
  narrativeSource: InterpretationNarrativeSourceSchema,
  createdAt: z.string().datetime(),
});
export type InterpretationListItem = z.infer<typeof InterpretationListItemSchema>;

// POST /simulations/compare/interpretations — corpo (as duas execuções a comparar).
export const CreateComparisonInterpretationRequestSchema = z
  .object({ a: z.string().uuid(), b: z.string().uuid() })
  .strict();
export type CreateComparisonInterpretationRequest = z.infer<
  typeof CreateComparisonInterpretationRequestSchema
>;
